import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { local } from 'store2';
import { find } from 'lodash-es';
import styled from 'styled-components';
import { Link } from '@material-ui/core';

import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { Banner } from '@dev/ee-components';

import { ApplicationState } from '@/store';
import { ShowDialog } from '@/store/dialog/actions';
import { PurchaseExtendedLogs } from '@/store/account/actions';
import { TIME_RANGE_35_DAYS } from '@/services/constants';
import { useMobile } from '@/services/hooks/useMobile';
import { isReseller } from '@/services/Secuirty';
import { getPaymentsUrl } from '@/services/tools/getPaymentsUrl';
// import { ActivateBillingDialog } from '@/services/Dialog/activateBillingDialog';
import { PurchaseExtendedLogsDialog } from '@/services/Dialog/PurchaseExtendedLogsDialog';
import { canSeeExtendedLogs } from '@/components/Campaigns/assets/EventFilter/helperFunctions';
import { useCurrentPlanStatus } from '@/services/hooks/useCurrentPlanStatus';
import { useDialog } from '@/services/Dialog/useDialog';
import { DialogIds } from '@/services/Dialog/DialogIds.enum';

const BannerContainer = styled.div`
    margin-bottom: 32px;
`;

const UpgradeLink: FC = (): JSX.Element => {
    const { account, config } = useSelector((state: ApplicationState) => state);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { showDialog } = useDialog();
    const showActivateBillingDialog = () => {
        showDialog(DialogIds.ActivateBillingDialog);
    };

    const purchaseExtendedLogs = (): void => {
        dispatch(PurchaseExtendedLogs());
    };

    const purchaseExtendedLogsDialog = (): void => {
        dispatch(
            ShowDialog({
                title: t('purchaseextendedlogs'),
                confirmLabel: t('purchase'),
                cancelLabel: t('cancel'),
                onClickConfirm: purchaseExtendedLogs,
                body: <PurchaseExtendedLogsDialog />,
            })
        );
    };

    const { IsPro, CanExtendLogs, IsSub, IsUser, AccountAccessLevel } = account?.GeneralInfo || {};

    const { isTrialOrFree } = useCurrentPlanStatus();
    const unlimitedAccount = !CanExtendLogs && !IsPro; //those account shoud have access to upgrade plan (only unlimited).

    const isUnlimitedPro = !CanExtendLogs && IsPro; // UnlimitedPro should not see upgrade link

    const paymentsUrl = isReseller() ? account?.GeneralInfo?.AccountPaymentUrl : getPaymentsUrl(config.isStandalone);

    const canSeeUpgradeLink =
        !AccountAccessLevel.includes(AccessLevel.ExtendedLogs) && !IsSub && !IsUser && !isUnlimitedPro && CanExtendLogs; // main accounts without extendedLogs with available Addon

    const trialAccountsParams = isTrialOrFree && {
        onClick: showActivateBillingDialog,
    };
    const unlimitedAccountsParams = unlimitedAccount && {
        href: paymentsUrl,
    };
    const legacyAccountsParam = {
        onClick: purchaseExtendedLogsDialog,
    };

    if (!canSeeUpgradeLink) {
        return null;
    }

    const linkParams = trialAccountsParams || unlimitedAccountsParams || legacyAccountsParam;

    return (
        <>
            {!unlimitedAccount && `${t('needslogupgrade')} `}
            <Link {...(isReseller() && { target: '_blank' })} {...linkParams}>
                {t('upgrade')}
            </Link>
        </>
    );
};

interface BannerComponentInterface {
    channelName: string;
}

const BannerComponent: FC<BannerComponentInterface> = ({ channelName }: BannerComponentInterface): JSX.Element => {
    const { t } = useTranslation();
    const isMobile = useMobile();
    const { account } = useSelector((state: ApplicationState) => state);
    const days = canSeeExtendedLogs(account.GeneralInfo.AccountAccessLevel)
        ? TIME_RANGE_35_DAYS
        : account?.GeneralInfo?.KeepLogsForDays;

    let extendedLogsStorage = local.get('showExtendLogsBanner');
    let hideBanner = false;

    if (!extendedLogsStorage) {
        extendedLogsStorage = [];
    } else {
        extendedLogsStorage = JSON.parse(extendedLogsStorage);
        hideBanner = !!find(extendedLogsStorage, (el) => el === channelName);
    }

    const [isBannerHidden, setToggleBanner] = useState(hideBanner);

    if (isBannerHidden) {
        return null;
    }

    return (
        <BannerContainer>
            <Banner
                title={t('activity')}
                body={
                    <>
                        {t('extendedloginfo', { days })} <UpgradeLink />
                    </>
                }
                isInfo
                isMobile={isMobile}
                onClose={(): void => {
                    extendedLogsStorage.push(channelName);
                    local.set('showExtendLogsBanner', JSON.stringify(extendedLogsStorage));
                    setToggleBanner(true);
                }}
            />
        </BannerContainer>
    );
};

export default BannerComponent;
